import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@material-ui/core';
import url from '../../constants/urlList';
import { STALE_SESSION_KEYS } from '../../utils/stale-session';
import { clearGoogleSameTabSignIn } from '../../utils/google-same-tab-signin';
import { GOOGLE_POPUP_NOT_FINISHED, GOOGLE_POPUP_SIGNED_IN } from '../../utils/google-popup-result';
import { GOOGLE_CONNECT_ACK, GOOGLE_CONNECT_PROOF, openGoogleConnectChannel,
  openWithGoogleConnectAttempt } from '../../utils/google-connect-handoff';
import { getConnections, sendInstitutionalCode, verifyInstitutionalCode, previewGoogle,
  confirmConnection, saveConnectionSession } from './account-connections-api';

const messages = {
  REAUTHENTICATION_REQUIRED: 'For your security, sign in again, then return here to connect your accounts.',
  SESSION_CHANGED: 'The signed-in account changed. Reload Settings before continuing.',
  ACCOUNT_UNAVAILABLE: 'This account cannot be connected. Contact support for help.',
  CONNECTION_CONFLICT: 'These accounts cannot be connected automatically. No connection has been confirmed. Contact support for help.',
  MERGE_IN_PROGRESS: 'Your connection is in progress. Retry to check its result; your request will not combine accounts twice.',
  MERGE_INTENT_EXPIRED: 'This confirmation expired. Verify the other sign-in again to get a fresh preview.',
  OTP_INVALID: 'That code could not be verified. Check the code or request another one.',
  OTP_EXPIRED: 'This code expired. Request another code.',
  OTP_RATE_LIMITED: 'Please wait before requesting another verification code.',
  RATE_LIMITED: 'Please wait a moment before trying again.',
  GOOGLE_PROOF_REQUIRED: 'Google verification could not be accepted. Choose Connect Google to sign in to the Google account again.',
  SERVICE_UNAVAILABLE: 'Account connections are temporarily unavailable. Please try again shortly.',
  POPUP_BLOCKED: 'Allow pop-ups for QLearn, then try Connect Google again.',
  POPUP_CLOSED: 'Google sign-in was closed. You can try again when you are ready.',
  POPUP_DETACHED: 'Waiting for Google. If you closed the Google window, choose Connect Google to try again.',
  POPUP_TIMED_OUT: 'Google sign-in took too long. Choose Connect Google to try again.',
  POPUP_NOT_FINISHED: 'Google sign-in didn\'t finish. Choose Connect Google to try again.',
  BAD_RESPONSE: 'We could not confirm the connection. Please sign in again to check your accounts.',
  NETWORK_ERROR: 'We could not reach QLearn. Check your connection and retry.',
};
const validConnections = value => [value?.google, value?.institutional].every(item =>
  item && typeof item.connected === 'boolean' && (!item.connected || (typeof item.email === 'string' && /^[^\s@]+@[^\s@]+$/.test(item.email))));
const validIntent = value => value?.status === 'CONFIRMATION_REQUIRED'
  && typeof value.intentId === 'string' && value.intentId.length > 0
  && Number.isFinite(Date.parse(value.expiresAt)) && Date.parse(value.expiresAt) > Date.now()
  && typeof value.preview?.googleEmail === 'string' && typeof value.preview?.institutionalEmail === 'string'
  && ['quizCount', 'answerCount', 'activeSubscriptionCount'].every(key =>
    Number.isSafeInteger(value.preview[key]) && value.preview[key] >= 0);
const sameActor = actor => actor.token && actor.id
  && window.localStorage.getItem('token') === actor.token
  && window.localStorage.getItem('userId') === actor.id;
const reloadSettings = () => window.location.reload();

export default function AccountConnectionsSection({ onSessionChanged = reloadSettings }) {
  const actor = useRef({ token: window.localStorage.getItem('token'), id: window.localStorage.getItem('userId') });
  const mounted = useRef(true);
  const running = useRef(false);
  const loadSequence = useRef(0);
  const popupCleanup = useRef(null);
  const [connections, setConnections] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState('');
  const [intent, setIntent] = useState(null);
  const [success, setSuccess] = useState(false);
  const [confirmationSubmitted, setConfirmationSubmitted] = useState(false);
  const [now, setNow] = useState(Date.now());

  const current = useCallback(() => {
    if (!mounted.current) return false;
    if (sameActor(actor.current)) return true;
    popupCleanup.current?.();
    running.current = false;
    setError('SESSION_CHANGED');
    setConnections(null); setSuccess(false); setEmail(''); setOtp('');
    setIntent(null); setChallenge(null); setConfirmationSubmitted(false); setBusy(false);
    return false;
  }, []);
  const load = useCallback(async () => {
    if (!current()) return null;
    const sequence = ++loadSequence.current;
    setBusy(true);
    const result = await getConnections();
    if (sequence !== loadSequence.current) return null;
    if (!current()) {
      if (mounted.current) {
        setBusy(false);
      }
      return null;
    }
    const valid = result.ok && validConnections(result.data);
    if (valid) { setConnections(result.data); setError(null); }
    else setError(result.code || 'BAD_RESPONSE');
    setBusy(false);
    return valid ? result.data : null;
  }, [current]);
  useEffect(() => {
    mounted.current = true;
    load();
    const timer = setInterval(() => setNow(Date.now()), 1000);
    const storageChanged = () => { current(); };
    window.addEventListener('storage', storageChanged);
    return () => {
      mounted.current = false;
      clearInterval(timer);
      window.removeEventListener('storage', storageChanged);
      popupCleanup.current?.();
    };
  }, [load, current]);

  const fail = result => {
    const code = result.code || 'BAD_RESPONSE';
    setError(code);
    if (['MERGE_INTENT_EXPIRED', 'CONNECTION_CONFLICT', 'REAUTHENTICATION_REQUIRED', 'ACCOUNT_UNAVAILABLE'].includes(code)) {
      setIntent(null); setChallenge(null); setOtp(''); setConfirmationSubmitted(false);
    }
  };
  const refreshConnectedMethod = async expectedMethod => {
    setIntent(null); setChallenge(null); setOtp('');
    const refreshed = await load();
    if (!refreshed || !current()) return;
    if (refreshed[expectedMethod]?.connected) setSuccess(true);
    else setError('BAD_RESPONSE');
  };
  const applyResult = async (result, { isConfirmation = false, expectedMethod } = {}) => {
    if (!current()) return;
    if (!result.ok) {
      fail(expectedMethod === 'google' && result.code === 'INVALID_REQUEST'
        ? { code: 'GOOGLE_PROOF_REQUIRED' } : result);
      return;
    }
    const data = result.data;
    if (validIntent(data) && !isConfirmation) {
      setIntent(data); setChallenge(null); setOtp(''); setConfirmationSubmitted(false); return;
    }
    if (data?.status !== 'CONNECTED') { fail({ code: 'BAD_RESPONSE' }); return; }
    if (data.session) {
      if (!saveConnectionSession(data.session, actor.current)) { fail({ code: 'BAD_RESPONSE' }); return; }
      onSessionChanged(); return;
    }
    // A merge confirmation MUST return the replacement session. A no-merge
    // connection may return status only, which we verify through GET instead.
    if (isConfirmation) { fail({ code: 'BAD_RESPONSE' }); return; }
    await refreshConnectedMethod(expectedMethod);
  };
  const perform = async action => {
    if (running.current || !current()) return;
    // Starting anything else ends the wait for a Google popup that looked closed.
    popupCleanup.current?.();
    running.current = true; setBusy(true); setError(null); setSuccess(false);
    try { await action(); }
    catch (_) { if (mounted.current) setError('NETWORK_ERROR'); }
    finally { running.current = false; if (mounted.current) setBusy(false); }
  };
  const send = event => {
    event.preventDefault();
    return perform(async () => {
      const normalized = email.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.(edu|org)$/i.test(normalized)) {
        setError('Use an institutional .edu or .org email address.'); return;
      }
      const result = await sendInstitutionalCode(normalized);
      if (!current()) return;
      if (!result.ok) { fail(result); return; }
      setEmail(normalized); setChallenge(result.challenge); setOtp('');
    });
  };
  const verify = event => {
    event.preventDefault();
    return perform(async () => {
      if (!challenge || Date.now() >= challenge.expiresAt) { setError('OTP_EXPIRED'); return; }
      await applyResult(await verifyInstitutionalCode({ email, challengeId: challenge.challengeId, otp: otp.trim() }), { expectedMethod: 'institutional' });
    });
  };
  const confirm = () => perform(async () => {
    if (!intent) return;
    setConfirmationSubmitted(true);
    // Always let the server resolve the intent, including an expired local
    // preview after a lost response. It may already have completed the merge.
    await applyResult(await confirmConnection(intent.intentId), { isConfirmation: true });
  });

  const connectGoogle = () => {
    if (running.current || !current()) return;
    popupCleanup.current?.();
    setError(null); setSuccess(false);
    // /socialAuth posts the token back to this window only from a popup
    // named "Sign In" that still has its opener, and only to this origin.
    // window.open copies this tab's sessionStorage into the popup, so drop any
    // same-tab sign-in mark and give the popup this attempt's connect mark
    // instead: /socialAuth never signs in with a connect popup's token. If the
    // popup loses window.opener (a sign-in page sent an enforced
    // Cross-Origin-Opener-Policy, or this tab was closed) it hands the proof
    // back over a BroadcastChannel carrying this attempt's nonce.
    clearGoogleSameTabSignIn();
    const { popup, nonce } = openWithGoogleConnectAttempt(() => window.open(`${url.domain}/qlearn/oauth2/authorization/google`, 'Sign In', 'popup=yes,width=520,height=720'));
    if (!popup) { setError('POPUP_BLOCKED'); return; }
    running.current = true; setBusy(true);
    const channel = nonce ? openGoogleConnectChannel() : null;
    let consumed = false;
    // A popup severed by COOP reads as closed here while it is still open.
    let detached = false;
    const cleanup = () => {
      window.removeEventListener('message', receive);
      channel?.close();
      clearInterval(poll);
      clearTimeout(timeout);
      try { if (!popup.closed) popup.close(); } catch (_) { /* browser already closed it */ }
      popupCleanup.current = null;
    };
    const finish = () => { running.current = false; if (mounted.current) setBusy(false); };
    const accept = async accessToken => {
      consumed = true; cleanup();
      running.current = true; setBusy(true); setError(null);
      try {
        if (current()) await applyResult(await previewGoogle(accessToken), { expectedMethod: 'google' });
      } catch (_) { if (mounted.current) setError('NETWORK_ERROR'); }
      finally { finish(); }
    };
    const receive = event => {
      if (consumed || event.origin !== window.location.origin || event.source !== popup) return;
      // The popup came back without a token: stop waiting (a popup that reads
      // as closed would otherwise keep "Waiting for Google" for 10 minutes).
      if (event.data?.msg === GOOGLE_POPUP_NOT_FINISHED) {
        consumed = true; cleanup(); finish(); if (mounted.current) setError('POPUP_NOT_FINISHED');
        return;
      }
      if (event.data?.msg !== GOOGLE_POPUP_SIGNED_IN
        || typeof event.data?.results?.accessToken !== 'string' || !event.data.results.accessToken) return;
      // accept() handles its own failures.
      void accept(event.data.results.accessToken);
    };
    const receiveHandoff = ({ data }) => {
      if (consumed || data?.type !== GOOGLE_CONNECT_PROOF || data.nonce !== nonce
        || typeof data.accessToken !== 'string' || !data.accessToken || !current()) return;
      channel.postMessage({ type: GOOGLE_CONNECT_ACK, nonce });
      void accept(data.accessToken);
    };
    window.addEventListener('message', receive);
    if (channel) channel.onmessage = receiveHandoff;
    const poll = setInterval(() => {
      if (!popup.closed || consumed || detached) return;
      if (channel) {
        detached = true; clearInterval(poll); finish(); if (mounted.current) setError('POPUP_DETACHED');
        return;
      }
      consumed = true; cleanup(); finish(); if (mounted.current) setError('POPUP_CLOSED');
    }, 500);
    const timeout = setTimeout(() => {
      if (consumed) return;
      consumed = true; cleanup();
      // Nothing listens any more, so stop saying this page is waiting.
      if (detached) {
        if (mounted.current) setError('POPUP_TIMED_OUT');
        return;
      }
      finish(); if (mounted.current) setError('REAUTHENTICATION_REQUIRED');
    }, 600000);
    popupCleanup.current = cleanup;
  };
  const signInAgain = () => {
    if (sameActor(actor.current)) STALE_SESSION_KEYS.forEach(key => window.localStorage.removeItem(key));
    window.location.assign(`/login?next=${encodeURIComponent('/user-settings#account-sign-in')}`);
  };
  const restart = () => {
    if (confirmationSubmitted) return;
    // Also ends the wait for a Google popup that looked closed.
    popupCleanup.current?.();
    setIntent(null); setChallenge(null); setOtp(''); setError(null); setSuccess(false);
  };
  const fatal = ['REAUTHENTICATION_REQUIRED', 'SESSION_CHANGED', 'ACCOUNT_UNAVAILABLE', 'CONNECTION_CONFLICT', 'BAD_RESPONSE'].includes(error);
  const resendSeconds = challenge ? Math.max(0, Math.ceil((challenge.resendAt - now) / 1000)) : 0;

  return <Box component="section" id="account-sign-in" aria-labelledby="account-sign-in-title" mt={3} style={{ maxWidth: 560, overflowWrap: 'anywhere' }}>
    <span id="institutional-email" aria-hidden="true" />
    <Typography id="account-sign-in-title" variant="h6" component="h2">Account &amp; sign-in</Typography>
    <Typography variant="body2" paragraph>Connect Google and your institutional email to use one QLearn account with the same questions, quizzes and progress.</Typography>
    {connections && <Box mb={2}>
      <Typography component="h3" variant="subtitle2">Google</Typography>
      <Typography variant="body2">{connections.google.connected ? connections.google.email : 'Not connected'}</Typography>
      <Typography component="h3" variant="subtitle2" style={{ marginTop: 12 }}>Institutional email</Typography>
      <Typography variant="body2">{connections.institutional.connected ? connections.institutional.email : 'Not connected'}</Typography>
    </Box>}
    {busy && <Box role="status" aria-label="Connecting account" my={1}><CircularProgress size={20} /> <span>Please wait…</span></Box>}
    {error === 'POPUP_DETACHED' && <Box role="status" my={2}><Typography variant="body2">{messages.POPUP_DETACHED}</Typography></Box>}
    {error && error !== 'POPUP_DETACHED' && <Box role="alert" my={2}><Typography variant="body2" color="error">{messages[error] || (error.startsWith('Use ') ? error : 'We could not complete this request. Please try again.')}</Typography></Box>}
    {success && <Typography role="status" paragraph>You can now use these sign-in methods for the same QLearn account.</Typography>}
    {(['REAUTHENTICATION_REQUIRED', 'BAD_RESPONSE'].includes(error) || (confirmationSubmitted && error)) && <Button onClick={signInAgain}>Sign in again</Button>}
    {error === 'SESSION_CHANGED' && <Button onClick={reloadSettings}>Reload Settings</Button>}
    {!connections && !busy && !fatal && <Button onClick={load}>Retry</Button>}
    {intent && !fatal && <Box aria-label="Review account connection">
      <Typography variant="subtitle1" component="h3">Review your connection</Typography>
      <Typography variant="body2">{intent.preview.googleEmail} + {intent.preview.institutionalEmail}</Typography>
      <Box component="ul" pl={3}>
        <li>{intent.preview.quizCount} {intent.preview.quizCount === 1 ? 'quiz' : 'quizzes'}</li>
        <li>{intent.preview.answerCount} {intent.preview.answerCount === 1 ? 'answer' : 'answers'}</li>
        <li>{intent.preview.activeSubscriptionCount} active {intent.preview.activeSubscriptionCount === 1 ? 'subscription' : 'subscriptions'}</li>
      </Box>
      <Typography variant="body2" paragraph>Your existing learning history will be combined. Both sign-in methods will open the same account. Existing paid subscriptions stay active; connecting accounts does not cancel any payments.</Typography>
      <Typography variant="body2" paragraph>Connecting accounts may give tutors already connected to either account access to the combined learning history.</Typography>
      {typeof intent.preview.message === 'string' && intent.preview.message.trim() && <Typography variant="body2" paragraph>{intent.preview.message}</Typography>}
      <Typography variant="body2" paragraph>This cannot be undone in Settings. Connect only accounts that belong to you.</Typography>
      {confirmationSubmitted && error && <Typography id="connection-confirmation-status" variant="body2" paragraph>Your connection request was sent and may already be complete. Retry to check its result, or sign in again.</Typography>}
      <Button variant="contained" color="primary" disabled={busy} onClick={confirm}>{confirmationSubmitted ? 'Retry connection' : 'Connect accounts'}</Button>
      <Button disabled={busy || confirmationSubmitted} onClick={restart}>Cancel</Button>
    </Box>}
    {connections && !intent && !fatal && <>
      {!connections.google.connected && <Box my={2}><Button variant="outlined" color="primary" disabled={busy} onClick={connectGoogle}>Connect Google</Button></Box>}
      {!connections.institutional.connected && !challenge && <Box component="form" onSubmit={send} mt={2}>
        <TextField id="connection-email" label="Institutional email" type="email" required fullWidth autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} disabled={busy} helperText="Use your .edu or .org address." inputProps={{ maxLength: 254 }} />
        <Box mt={2}><Button type="submit" variant="contained" color="primary" disabled={busy}>Send verification code</Button></Box>
      </Box>}
      {challenge && <Box component="form" onSubmit={verify} mt={2}>
        <Typography variant="body2" paragraph>Enter the code sent to {email}.</Typography>
        <TextField id="connection-code" label="Verification code" required fullWidth autoFocus autoComplete="one-time-code" value={otp} onChange={event => setOtp(event.target.value)} disabled={busy} inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 }} />
        <Box mt={2}><Button type="submit" variant="contained" color="primary" disabled={busy || otp.trim().length !== 6}>Verify email</Button>
          <Button onClick={send} disabled={busy || resendSeconds > 0}>{resendSeconds > 0 ? `Resend in ${resendSeconds}s` : 'Resend code'}</Button>
          <Button onClick={restart} disabled={busy}>Use another email</Button></Box>
      </Box>}
    </>}
  </Box>;
}
